import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Code2, Server, Workflow, Users, Check } from "lucide-react";

const services = [
  {
    icon: Code2,
    title: "Custom Software Development",
    description: "End-to-end development of bespoke applications tailored to your specific business requirements.",
    features: ["Requirements analysis", "Full-stack development", "API integration", "Quality assurance"]
  },
  {
    icon: Server,
    title: "Platform Engineering",
    description: "Design and implementation of scalable platforms, including cloud infrastructure and microservices.",
    features: ["Cloud architecture", "Infrastructure automation", "CI/CD pipelines", "Performance optimization"]
  },
  {
    icon: Workflow,
    title: "System Integration",
    description: "Connecting disparate systems through APIs, middleware, and data synchronization.",
    features: ["API design", "Legacy integration", "Data migration", "Real-time sync"]
  },
  {
    icon: Users,
    title: "Technical Consulting",
    description: "Expert guidance on technology strategy, architecture decisions, and engineering practices.",
    features: ["Architecture review", "Technology evaluation", "Security assessment", "Team augmentation"]
  }
];

const technologies = [
  "Python", "Go", "TypeScript", "React", "Node.js", "PostgreSQL",
  "MongoDB", "Redis", "Kubernetes", "AWS", "GCP", "Terraform"
];

export default function StudioPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="studio-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="studio-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1705073703451-4d49f5eed883?auto=format&fit=crop&w=2000&q=80"
            alt="Abstract sculpture"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/95 to-obsidian/90" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-coral mb-6 opacity-0 animate-fade-up">
              Raindas Studio
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Custom software
              <br />
              engineering for
              <br />
              <span className="text-mint">enterprise.</span>
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed max-w-2xl opacity-0 animate-fade-up animate-delay-200">
              We partner with organizations to design and deliver software solutions tailored to their specific requirements. From custom applications to platform engineering, our team brings deep technical expertise to complex challenges.
            </p>
            <div className="mt-12 opacity-0 animate-fade-up animate-delay-300">
              <Link to="/contact">
                <button 
                  data-testid="studio-hero-cta"
                  className="btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors duration-300"
                >
                  Discuss Your Project
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 bg-obsidian-light" data-testid="studio-services">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Services
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                What we offer
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <div
                key={service.title}
                className={`card-beam p-10 group ${idx === 0 ? 'lg:row-span-2' : ''}`}
                data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <service.icon className={`${idx === 0 ? 'w-12 h-12' : 'w-10 h-10'} text-mint mb-8 group-hover:text-coral transition-colors`} />
                <h3 className={`${idx === 0 ? 'text-3xl' : 'text-2xl'} font-heading font-bold text-white mb-4 tracking-tight`}>
                  {service.title}
                </h3>
                <p className={`text-slate-400 leading-relaxed mb-8 ${idx === 0 ? 'text-lg' : ''}`}>
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-3 text-slate-300 text-sm">
                      <Check className="w-4 h-4 text-mint flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="studio-process">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-electric mb-4">
              Process
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
              How we work
            </h2>
            <p className="mt-6 text-slate-400 text-lg leading-relaxed">
              Our engagement process is designed to reduce risk, maintain transparency, and deliver results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Discovery", description: "We begin by understanding your business context, technical requirements, and constraints." },
              { step: "02", title: "Design", description: "Our team develops detailed technical specifications and architecture plans." },
              { step: "03", title: "Development", description: "Iterative development with regular checkpoints and continuous integration." },
              { step: "04", title: "Delivery", description: "Deployment with comprehensive documentation and knowledge transfer." }
            ].map((item, idx) => (
              <div 
                key={item.step} 
                className="relative p-8 bg-white/5 border border-white/5 hover:border-white/10 transition-colors group"
                data-testid={`process-step-${item.step}`}
              >
                <span className="text-6xl font-heading font-bold text-white/5 group-hover:text-mint/20 transition-colors absolute top-4 right-4">
                  {item.step}
                </span>
                <h3 className="text-xl font-heading font-bold text-white mb-4 relative z-10">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed relative z-10">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-24 md:py-32 bg-obsidian-light" data-testid="studio-technologies">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Technologies
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                Technical expertise
              </h2>
              <p className="mt-6 text-slate-400 text-lg leading-relaxed">
                Our team has deep experience across a broad range of technologies. We select tools based on your specific requirements, not trends.
              </p>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {technologies.map((tech) => (
                <div
                  key={tech}
                  className="bg-white/5 border border-white/5 px-4 py-3 text-center hover:border-mint/50 hover:bg-white/10 transition-all"
                >
                  <span className="text-slate-300 text-sm font-medium">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="studio-cta">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass-card p-12 md:p-20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-mint/5 to-transparent" />
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                Ready to start
                <br />
                your project?
              </h2>
              <p className="mt-6 text-xl text-slate-300 leading-relaxed">
                Contact us to discuss your requirements. We'll provide an initial assessment and proposal tailored to your needs.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <button 
                    data-testid="studio-cta-contact"
                    className="btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors"
                  >
                    Get in Touch
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </button>
                </Link>
                <Link to="/case-studies">
                  <button 
                    data-testid="studio-cta-case-studies"
                    className="group flex items-center gap-3 bg-transparent border border-white/20 text-white px-8 py-5 font-medium text-sm tracking-wide uppercase hover:bg-white/5 transition-all"
                  >
                    View Our Work
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
